import { useState, useEffect, useMemo, useRef } from 'react';
import { fetchPatientStatementData, fetchPatientStatements, createPatientStatement } from '../services/api';
import { generateStatement } from '../utils/generateStatement';
import './StatementModal.css';

function fmt(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(String(dateStr).slice(0, 10) + 'T12:00:00');
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

export default function StatementModal({ patient, onClose }) {
  const patientId = patient?.id || patient?.patient_id;

  const [statementData, setStatementData] = useState(null);
  const [facilityId, setFacilityId] = useState(null);
  const [facilityChoices, setFacilityChoices] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const afterPrintListenerRef = useRef(null);

  // Remove any dangling afterprint listener when the modal unmounts
  useEffect(() => {
    return () => {
      if (afterPrintListenerRef.current) {
        window.removeEventListener('message', afterPrintListenerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    setFacilityChoices(null);

    Promise.all([
      fetchPatientStatementData(patientId, facilityId),
      fetchPatientStatements(patientId),
    ])
      .then(([dataRes, histRes]) => {
        const data = dataRes.data;
        setStatementData(data);
        // Pre-select all claims with outstanding patient responsibility
        const outstanding = new Set(
          data.claims
            .filter((c) => (parseFloat(c.patient_responsibility) || 0) > 0)
            .map((c) => c.id)
        );
        setSelectedIds(outstanding);
        setHistory(histRes.data || []);
        setLoading(false);
      })
      .catch((err) => {
        // A patient billed at several facilities: the statement's issuer is a
        // choice, never a guess — offer the facilities and refetch for the one picked.
        if (err?.response?.status === 409 && err.response.data?.code === 'FACILITY_REQUIRED') {
          setFacilityChoices(err.response.data.facilities || []);
          setLoading(false);
          return;
        }
        setError(err?.response?.data?.error || 'Failed to load statement data.');
        setLoading(false);
      });
  }, [patientId, facilityId]);

  const toggleClaim = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const all = statementData?.claims || [];
    if (selectedIds.size === all.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(all.map((c) => c.id)));
    }
  };

  const selectedClaims = useMemo(
    () => (statementData?.claims || []).filter((c) => selectedIds.has(c.id)),
    [statementData, selectedIds]
  );

  const totals = useMemo(() => {
    return selectedClaims.reduce(
      (acc, c) => {
        acc.charges   += parseFloat(c.total_charge)           || 0;
        acc.insurance += parseFloat(c.insurance_payments)     || 0;
        acc.adj       += parseFloat(c.adjustments)            || 0;
        acc.ptResp    += parseFloat(c.patient_responsibility) || 0;
        acc.ptPaid    += parseFloat(c.patient_paid)           || 0;
        return acc;
      },
      { charges: 0, insurance: 0, adj: 0, ptResp: 0, ptPaid: 0 }
    );
  }, [selectedClaims]);

  const handleGenerate = async () => {
    if (selectedClaims.length === 0) return;
    setGenerating(true);
    setGenError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);

      const serviceDates = selectedClaims
        .map((c) => String(c.service_date).slice(0, 10))
        .sort();
      const serviceFrom = serviceDates[0] || null;
      const serviceTo   = serviceDates[serviceDates.length - 1] || null;

      // Snapshot the payload so the afterprint callback has everything it needs
      const payload = {
        facility_id:                  statementData.facility.facility_id,
        statement_date:               today,
        service_date_from:            serviceFrom,
        service_date_to:              serviceTo,
        total_charges:                totals.charges,
        total_insurance_payments:     totals.insurance,
        total_adjustments:            totals.adj,
        // Match the sheet's Balance Due (net of the patient's own payments), and
        // keep this path identical to the bulk one — see BulkStatementsPanel.
        total_patient_responsibility: Math.max(0, totals.ptResp - totals.ptPaid),
        claim_ids:                    selectedClaims.map((c) => c.id),
      };

      // Remove any stale listener from a previous click
      if (afterPrintListenerRef.current) {
        window.removeEventListener('message', afterPrintListenerRef.current);
      }

      // Fires when the user actually prints (afterprint event in the print window)
      const onAfterPrint = async (event) => {
        if (event.data?.type !== '__STMT_AFTER_PRINT__') return;
        window.removeEventListener('message', onAfterPrint);
        afterPrintListenerRef.current = null;
        try {
          await createPatientStatement(patientId, payload);
          const histRes = await fetchPatientStatements(patientId);
          setHistory(histRes.data || []);
        } catch {
          setGenError('Statement printed but could not be saved to history. Please contact support.');
        }
      };

      afterPrintListenerRef.current = onAfterPrint;
      window.addEventListener('message', onAfterPrint);

      // Open the print window — no DB insert until afterprint fires
      generateStatement({
        patient:       statementData.patient,
        facility:      statementData.facility,
        claims:        selectedClaims,
        statementDate: today,
        // Account-level credit, shown on the statement but never netted off the
        // balance — it is not spent until someone posts a transfer.
        creditSummary: statementData.credit_summary,
      });
    } catch (err) {
      setGenError(err?.response?.data?.error || 'Failed to generate statement. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const allClaims  = statementData?.claims || [];
  const allChecked = allClaims.length > 0 && selectedIds.size === allClaims.length;
  const someChecked = selectedIds.size > 0 && selectedIds.size < allClaims.length;

  return (
    <div className="sm-overlay">
      <div className="sm-modal">

        {/* Header */}
        <div className="sm-header">
          <div className="sm-header-left">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            <div>
              <div className="sm-header-title">Generate Statement</div>
              <div className="sm-header-sub">
                {patient?.first_name} {patient?.last_name} &mdash; Account{' '}
                #{String(patient?.id || patient?.patient_id || 0).padStart(5, '0')}
              </div>
            </div>
          </div>
          <button className="sm-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="sm-body">
          {loading && (
            <div className="sm-loading">
              <svg className="sm-spinner" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
              <span>Loading claim data&hellip;</span>
            </div>
          )}

          {error && !loading && (
            <div className="sm-error-banner">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          {!loading && !error && facilityChoices && (
            <div className="sm-facility-choice">
              <div className="sm-section-title">Issuing Facility</div>
              <p>This patient has claims at more than one facility. A statement comes from one billing entity — choose which.</p>
              <select
                aria-label="Issuing facility"
                defaultValue=""
                onChange={(e) => e.target.value && setFacilityId(Number(e.target.value))}
              >
                <option value="" disabled>Select a facility…</option>
                {facilityChoices.map((f) => (
                  <option key={f.facility_id} value={f.facility_id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}

          {!loading && !error && !facilityChoices && statementData && (
            <>
              {/* Claims to include */}
              <div className="sm-section-title">Claims to Include</div>

              {allClaims.length === 0 ? (
                statementData.claims_on_file > 0 ? (
                  // Claims exist but none are billable (all voided/superseded) → $0 owed.
                  <div className="sm-empty sm-empty-settled">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    <p><strong>Account settled.</strong></p>
                    <p>No outstanding patient balance ($0.00 due).</p>
                  </div>
                ) : (
                  <div className="sm-empty">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                    </svg>
                    <p>No claims on file for this patient yet.</p>
                  </div>
                )
              ) : (
                <div className="sm-table-wrap">
                  <table className="sm-table">
                    <thead>
                      <tr>
                        <th style={{ width: '32px' }}>
                          <input
                            type="checkbox"
                            checked={allChecked}
                            ref={(el) => { if (el) el.indeterminate = someChecked; }}
                            onChange={toggleAll}
                            aria-label="Select all claims"
                          />
                        </th>
                        <th>Claim #</th>
                        <th>Service Date</th>
                        <th>Provider</th>
                        <th className="r">Charges</th>
                        <th className="r">Ins. Paid</th>
                        <th className="r">Adjustments</th>
                        <th className="r">Pt. Responsibility</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allClaims.map((claim) => {
                        const checked = selectedIds.has(claim.id);
                        const ptResp = parseFloat(claim.patient_responsibility) || 0;
                        const hasBalance = ptResp > 0;
                        return (
                          <tr
                            key={claim.id}
                            className={`sm-claim-row ${checked ? 'sm-row-selected' : ''}`}
                            onClick={() => toggleClaim(claim.id)}
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleClaim(claim.id)}
                              />
                            </td>
                            <td className="sm-claim-num">{claim.claim_number}</td>
                            <td>{fmtDate(claim.service_date)}</td>
                            <td className="sm-provider">{claim.rendering_provider || '—'}</td>
                            <td className="r">{fmt(claim.total_charge)}</td>
                            <td className="r">{fmt(claim.insurance_payments)}</td>
                            <td className="r">{fmt(claim.adjustments)}</td>
                            <td className={`r sm-pt-resp ${hasBalance ? 'sm-has-balance' : ''}`}>
                              {fmt(ptResp)}
                            </td>
                            <td>
                              <span className={`sm-status-badge sm-status-${claim.payment_status}`}>
                                {claim.payment_status?.charAt(0).toUpperCase() + claim.payment_status?.slice(1)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Totals panel */}
              {allClaims.length > 0 && (
                <div className="sm-totals-panel">
                  <div className="sm-totals-grid">
                    <div className="sm-total-item">
                      <span className="sm-total-label">Total Charges</span>
                      <span className="sm-total-value">{fmt(totals.charges)}</span>
                    </div>
                    <div className="sm-total-item">
                      <span className="sm-total-label">Insurance Payments</span>
                      <span className="sm-total-value">{fmt(totals.insurance)}</span>
                    </div>
                    <div className="sm-total-item">
                      <span className="sm-total-label">Adjustments</span>
                      <span className="sm-total-value">{fmt(totals.adj)}</span>
                    </div>
                    <div className="sm-total-item sm-total-due">
                      <span className="sm-total-label">Amount Due</span>
                      <span className="sm-total-value sm-total-due-value">{fmt(Math.max(0, totals.ptResp - totals.ptPaid))}</span>
                    </div>
                  </div>
                  <div className="sm-selected-note">
                    {selectedClaims.length} of {allClaims.length} claim{allClaims.length !== 1 ? 's' : ''} selected
                  </div>
                </div>
              )}

              {genError && (
                <div className="sm-error-banner" style={{ marginTop: '8px' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {genError}
                </div>
              )}

              {/* Print action */}
              <div className="sm-actions">
                <button className="sm-btn-cancel" onClick={onClose} disabled={generating}>
                  Cancel
                </button>
                <button
                  className="sm-btn-print"
                  onClick={handleGenerate}
                  disabled={generating || selectedClaims.length === 0}
                >
                  {generating ? (
                    <>
                      <svg className="sm-spinner-sm" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                      </svg>
                      Generating&hellip;
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                        <rect x="6" y="14" width="12" height="8"/>
                      </svg>
                      Print Statement
                    </>
                  )}
                </button>
              </div>

              {/* Statement History */}
              <div className="sm-history-section">
                <div className="sm-section-title">Statement History</div>
                {history.length === 0 ? (
                  <div className="sm-history-empty">No statements have been generated for this patient yet.</div>
                ) : (
                  <table className="sm-table sm-history-table">
                    <thead>
                      <tr>
                        <th>Statement #</th>
                        <th>Date</th>
                        <th>Claims</th>
                        <th className="r">Charges</th>
                        <th className="r">Amount Due</th>
                        <th>Printed By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((stmt) => {
                        const claimCount = (() => {
                          try {
                            const ids = typeof stmt.claim_ids === 'string'
                              ? JSON.parse(stmt.claim_ids)
                              : stmt.claim_ids;
                            return Array.isArray(ids) ? ids.length : '—';
                          } catch { return '—'; }
                        })();
                        const printedBy = stmt.printed_by_first
                          ? `${stmt.printed_by_first} ${stmt.printed_by_last}`.trim()
                          : '—';
                        return (
                          <tr key={stmt.statement_id} className="sm-history-row">
                            <td className="sm-stmt-num">{stmt.statement_number}</td>
                            <td>{fmtDate(stmt.statement_date)}</td>
                            <td>{claimCount}</td>
                            <td className="r">{fmt(stmt.total_charges)}</td>
                            <td className="r sm-has-balance">{fmt(stmt.total_patient_responsibility)}</td>
                            <td>{printedBy}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
